/**
 * Hook to determine if the trial source control banner should be shown.
 * Show when: user is on trial, has 5+ chats (from /chat/chats response),
 * has not yet used source control, and user.has_seen_try_source_control_during_trial_banner is not true.
 */

import { openSourceControlPopoverAtom } from '@/atoms/sourceControlAtoms';;
import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { useQueryChats } from '@/hooks/query/useQueryChats';
import { useQueryUser } from '@heidi/workspace/data';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { UserActivityAction } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

const TRIAL_CHATS_THRESHOLD = 5;

export function useTrialSourceControlBanner() {
  const { isInFreeTrial } = useEvidenceBillingInfo();

  const { data: chatsData } = useQueryChats(1, 100);
  const chatCount = chatsData?.total ?? 0;
  const hasReachedThreshold = chatCount >= TRIAL_CHATS_THRESHOLD;

  const { data: userData } = useQueryUser((user) =>
    user
      ? {
          userActivity: user.user_activity,
          hasSeenBanner: user.has_seen_try_source_control_during_trial_banner ?? false,
        }
      : undefined
  );
  const sourceControlCount =
    userData?.userActivity?.[UserActivityAction.evidence_source_control] ?? 0;
  const hasUsedSourceControl = sourceControlCount > 0;
  const hasSeenBanner = userData?.hasSeenBanner ?? false;
  const setOpenSourceControlPopover = useSetAtom(openSourceControlPopoverAtom);

  const { mutate: updateUser } = useMutateUpdateUser({ enableInvalidation: false });

  const shouldShow =
    Boolean(isInFreeTrial) &&
    Boolean(hasReachedThreshold) &&
    !hasUsedSourceControl &&
    !hasSeenBanner;

  const onTryNow = useCallback(() => {
    void updateUser({ has_seen_try_source_control_during_trial_banner: true });
    setOpenSourceControlPopover(true);
  }, [updateUser, setOpenSourceControlPopover]);
  
  const onDismiss = useCallback(() => {
    void updateUser({ has_seen_try_source_control_during_trial_banner: true });
  }, [updateUser]);

  return { shouldShow, onTryNow, onDismiss };
};
